import { ThumbFieldsMui } from '@app/components/form/ThumbFieldsMui';
import { SocialPublishAtField } from '@app/modules/social-export/components/inputs/SocialPublishAtField';
import {
  getBaseExportSchema,
  MessageFormatter,
} from '@app/modules/social-export/components/validation/base-export-schema';
import { BaseExportFormProps } from '@app/modules/social-export/hooks/useSocialExportFactory';
import { FormSection } from '@app/modules/social-export/screens/manage/platforms/FormSection';
import { zodResolver } from '@hookform/resolvers/zod';
import { socialExport } from '@mi-front/api-clients';
import { Grid, Stack } from '@mui/material';
import { useMemo } from 'react';
import { FormContainer, SwitchElement, TextFieldElement } from 'react-hook-form-mui';
import { useIntl } from 'react-intl';
import { z } from 'zod';

type InstagramPublishFormProps = BaseExportFormProps<
  socialExport.InstagramPublishDto,
  socialExport.InstagramSettingsDto
>;

const requiredMsg = { id: 'common.required', defaultMessage: 'Requerido' };

function getInstagramPublishValidationSchema(t: MessageFormatter) {
  return getBaseExportSchema(t)
    .extend({
      content: z.string().trim().min(1, t(requiredMsg)),
      shareToFeed: z.boolean(),
      userTags: z.string().optional(),
      thumbType: z.enum(['DEFAULT', 'IMAGE_URL', 'VIDEO_FRAME']),
      coverUrl: z.string().nullable().optional(),
      thumbOffsetMs: z.number().nullable().optional(),
    })
    .refine(
      (data) => {
        if (data.thumbType === 'IMAGE_URL') {
          return !!data.coverUrl;
        }
        return true;
      },
      { message: t(requiredMsg), path: ['coverUrl'] },
    )
    .refine(
      (data) => {
        if (data.thumbType === 'VIDEO_FRAME') {
          return data.thumbOffsetMs !== null && data.thumbOffsetMs !== undefined;
        }
        return true;
      },
      { message: t(requiredMsg), path: ['thumbOffsetMs'] },
    );
}

type InstagramPublishValues = z.infer<ReturnType<typeof getInstagramPublishValidationSchema>>;

export function InstagramPublishForm({
  fragment,
  onUpload,
  defaultSettings,
  videoUrl,
  actions,
}: InstagramPublishFormProps) {
  const { $t } = useIntl();
  const schema = useMemo(() => getInstagramPublishValidationSchema((msg) => $t(msg)), [$t]);
  const initialValues: InstagramPublishValues = {
    content: fragment.description ?? '',
    shareToFeed: true,
    userTags: '',
    thumbType: 'DEFAULT',
    coverUrl: null,
    thumbOffsetMs: null,
    publishAt: null,
  };

  const beforeSubmit = (data: InstagramPublishValues): socialExport.InstagramPublishDto => ({
    description: data.content,
    thumbOffsetMs: data.thumbType === 'VIDEO_FRAME' ? (data.thumbOffsetMs ?? undefined) : undefined,
    coverUrl: data.thumbType === 'IMAGE_URL' ? (data.coverUrl ?? undefined) : undefined,
    shareToFeed: data.shareToFeed,
    userTags: data.userTags
      ? data.userTags
          .split(',')
          .map((s) => s.trim().replace('@', ''))
          .filter(Boolean)
      : undefined,
    publishAt: data.publishAt || undefined,
  });

  return (
    <FormContainer
      values={initialValues}
      resolver={zodResolver(schema)}
      onSuccess={async (formData) => {
        await onUpload(beforeSubmit(formData));
      }}
    >
      <FormSection
        title={$t({
          id: 'socialExport.form.content',
          defaultMessage: 'Contenido',
        })}
      >
        <Stack spacing={3}>
          <TextFieldElement
            name="content"
            label={$t({
              id: 'socialExport.form.content',
              defaultMessage: 'Contenido',
            })}
            required
            fullWidth
            multiline
            rows={3}
          />
          <SwitchElement
            name="shareToFeed"
            label={$t({
              id: 'socialExport.form.publishToFeed',
              defaultMessage: 'Publicar también en el feed',
            })}
            color="primary"
          />
          <TextFieldElement
            name="userTags"
            label={$t({
              id: 'socialExport.form.taggedUsers',
              defaultMessage: 'Usuarios etiquetados',
            })}
            helperText={$t({
              id: 'socialExport.form.idsSeparatedByComma',
              defaultMessage: 'Ids separados por coma',
            })}
            fullWidth
          />
        </Stack>
      </FormSection>
      <FormSection
        title={$t({
          id: 'socialExport.form.thumbnail',
          defaultMessage: 'Thumbnail',
        })}
      >
        <ThumbFieldsMui urlFieldName="coverUrl" maxSizeMb={8} videoUrl={videoUrl} />
      </FormSection>
      <FormSection
        title={$t({
          id: 'socialExport.form.scheduling',
          defaultMessage: 'Programación',
        })}
      >
        <Grid container>
          <Grid item md={12}>
            <SocialPublishAtField />
          </Grid>
        </Grid>
      </FormSection>
      {actions}
    </FormContainer>
  );
}
